/* eslint-disable no-console */

import { format } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

import { dashboardBffClient } from '@/api-client';

import { useMainStore } from './stores/main';

const sessionId = uuidv4();

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'none';

const levels: Record<LogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3, none: 4 };

const minLevelServerSend: LogLevel = 'info';
const minLevelConsoleLog: LogLevel = 'debug';

// Must be a function so that the timeStamp isn't cached.
const prefix = () => `[Kasten ${format(new Date(), 'MM/dd/yy hh:mma ss.SSS xx')}]`;

/**
 * Replace JSON fields known to contain secret info
 */
function redactSecrets(jsonStr: string): string {
  if (!jsonStr) return jsonStr;
  const secretFields = [
    'keyring',
    'password',
    'secret',
    'secretAccessKey',
    'serviceKey',
    'storageKey',
  ];

  return secretFields.reduce((acc, field) => {
    const regex = new RegExp(`"${field}":".+?"`, 'g');
    return acc.replace(regex, `"${field}":"REDACTED"`);
  }, jsonStr);
}

/**
 * Prepares a log argument for output by handling stringification and redaction
 */
function prepareLogArg(arg: unknown = ''): unknown {
  if (typeof arg === 'string') return redactSecrets(arg);
  try {
    const str = JSON.stringify(arg);
    const redacted = redactSecrets(str);
    return JSON.parse(redacted);
  } catch (err) {
    console.warn('[Kasten] logger.ts: error preparing log arg');
  }
}

type LevelledConsoleMethod = Exclude<LogLevel, 'none'>;
type GroupingConsoleMethod = 'group' | 'groupCollapsed' | 'groupEnd' | 'table';

/**
 * Logs a level-gated message (debug/info/warn/error) to the console.
 */
function logToConsole(level: LevelledConsoleMethod, ...args: unknown[]): void {
  if (levels[level] < levels[minLevelConsoleLog]) return;
  console[level](prefix(), ...args.map(prepareLogArg));
}

/**
 * Calls a console grouping method (group/groupCollapsed/groupEnd/table) without
 * level gating — these are structural and shouldn't be filtered by minLevelConsoleLog.
 */
function callConsoleGrouping(method: GroupingConsoleMethod, ...args: unknown[]): void {
  console[method](prefix(), ...args.map(prepareLogArg));
}

/**
 * Send a log message to the server
 */
export async function send(
  level: Exclude<LogLevel, 'none'>,
  msg: string,
  ...args: unknown[]
): Promise<void> {
  const store = useMainStore();
  if (store.xhrEnabled === false) return;
  if (levels[level] < levels[minLevelServerSend]) return;

  try {
    const payload = {
      level,
      sessionId,
      time: new Date().toISOString(),
      message: msg,
      fields: [{ name: 'data', value: args.map(prepareLogArg) }],
    };

    await dashboardBffClient.log.logMessage(payload);
  } catch (err) {
    console.error('[Kasten] logger.ts: error in log.send()');
  }
}

export function info(msg: string, ...args: unknown[]): void {
  logToConsole('info', msg, ...args);
  send('info', msg, ...args);
}

export function debug(msg: string, ...args: unknown[]): void {
  logToConsole('debug', msg, ...args);
  send('debug', msg, ...args);
}

export function warn(msg: string, ...args: unknown[]): void {
  logToConsole('warn', msg, ...args);
  send('warn', msg, ...args);
}

export function error(msg: string, ...args: unknown[]): void {
  logToConsole('error', msg, ...args);
  send('error', msg, ...args);
}

export function group(msg: string, ...args: unknown[]): void {
  callConsoleGrouping('group', msg, ...args);
  send('info', msg, ...args);
}

export function groupCollapsed(msg: string, ...args: unknown[]): void {
  callConsoleGrouping('groupCollapsed', msg, ...args);
  send('info', msg, ...args);
}

export function groupEnd(): void {
  console.groupEnd();
}

export function table(msg: unknown, ...args: unknown[]): void {
  callConsoleGrouping('table', msg, ...args);
  send('info', String(msg), ...args);
}

const startTimes: Record<string, number> = {};

export function time(name: string): void {
  startTimes[name] = new Date().getTime();
}

export function timeEnd(name: string): void {
  if (typeof startTimes[name] === 'undefined') {
    console.warn(prefix(), `timeEnd(): Unknown timer '${name}'`);
    return;
  }
  const elapsed = new Date().getTime() - startTimes[name];
  delete startTimes[name];
  const message = `Time: ${name}: ${elapsed} ms`;
  logToConsole('info', message);
  send('info', message);
}

function onError(msg: string, url: string, lineNo: number, columnNo: number, err: Error): boolean {
  error('Uncaught error (msg, url, lineNo, columnNo, err):', msg, url, lineNo, columnNo, err);
  return false;
}
window.onerror = onError;
